import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format } from 'app/shared/utils/string.util';
import {
    commonFileTypeStoreDeleteActivityReadSettings,
    getCommonFileTypeStoreReadSettingControls,
    getCommonBinaryStoreReadSettingControls,
    cdcDestination
} from '../consts/metadata-consts';
import { Constants } from 'app/shared/shared-constants';
import { binaryMetadata } from '../dataset/binary-metadata';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ftpDisableChunkingInfoLink } from 'app/shared/constants/links.const';
import { ConnectionAuthKind, shellStringResources } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

const insertUseBinaryTransfer = (controls: ControlMetadata[]) => {
    if (controls) {
        controls.splice(-1, 0,
            {
                type: 'parameterizable',
                child: {
                    type: 'checkbox',
                    bind: 'useBinaryTransfer',
                    label: stringResources.Ftp_Use_Binary_Transfer_Label,
                    defaultValue: true,
                    labelPosition: 'left'
                },
                dynamicContentTextClass: 'input-spaced'
            });
    }
    return controls;
};

const dynamicContentControls: ControlMetadata[] = [
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'host',
            label: stringResources.ModelName_Host,
            required: true,
            placeholder: stringResources.FTPServer_LinkedService_Host_Placeholder,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        }
    },
    {
        type: 'parameterizable',
        child: {
            type: 'textbox',
            bind: 'port',
            label: stringResources.ModelName_Port,
            isLabelVertical: true,
            inputType: 'number',
            defaultValue: 21,
            min: 0,
            max: 65535
        }
    },
    {
        type: 'radio-switch',
        nested: 1,
        bind: 'enableSsl',
        label: stringResources.ModelName_EnableSSL,
        isLabelVertical: true,
        options: [
            {
                label: stringResources.LinkedService_EnableSSL,
                value: true
            },
            {
                label: stringResources.LinkedService_DisableSSL,
                value: false
            }
        ],
        defaultValue: true,
        cases: [
            {
                caseValue: true,
                children: [
                    {
                        type: 'radio',
                        bind: 'enableServerCertificateValidation',
                        label: stringResources.ModelName_EnableServerCertificateValidation,
                        isLabelVertical: true,
                        options: [
                            {
                                label: stringResources.Enable,
                                value: true
                            },
                            {
                                label: stringResources.Disable,
                                value: false
                            }
                        ],
                        defaultValue: true
                    }
                ]
            }
        ]
    },
    {
        type: 'select-switch',
        bind: 'authenticationType',
        label: stringResources.ModelName_AuthenticationType,
        options: [
            {
                label: stringResources.Basic_DisplayText,
                value: 'Basic'
            },
            {
                label: stringResources.Anonymous_DisplayText,
                value: 'Anonymous'
            }
        ],
        required: true,
        validators: [
            { type: 'required', errorMessage: '' }
        ],
        isLabelVertical: true,
        defaultValue: 'Basic',
        cases: [
            {
                caseValue: 'Basic',
                children: [
                    {
                        type: 'parameterizable',
                        child: {
                            type: 'textbox',
                            bind: 'userName',
                            label: stringResources.ModelName_UserName,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        }
                    },
                    {
                        type: 'dynamic-akv-switcher',
                        enableAKVParameterization: true,
                        bind: 'password',
                        label: stringResources.ModelName_Password,
                        child: {
                            type: 'parameterizable',
                            attribute: 'password',
                            disableAddParameter: true,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_Password,
                                required: true,
                                isLabelVertical: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ]
                            }
                        },
                        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                            format(stringResources.ModelName_LinkedServiceTemplate, shellStringResources.FtpServer_DisplayText),
                            stringResources.ModelName_Password)
                    }
                ]
            }
        ]
    }
];

export const ftpServerMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.ftpServer.linkedServiceType,
        displayName: shellStringResources.FtpServer_DisplayText,
        linkedServiceKind: 'file',
        icon: {
            className: 'svg-i-100x100-FTP'
        },
        isFirstClass: true,
        category: ['File'],
        supportedInTrident: true,
        supportCopyJobSource: true,
        ...(GlobalVariables.copyJobGenericCdcPhase2 ? {
            cdc: {
                supportCDC: true,
                supportedCDCDestinations: cdcDestination,
                supportGenericCdc: true
            }
        } : {}),
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'FTP' as ResourceKind
        },
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic, ConnectionAuthKind.Anonymous],
        notSupportCopySink: true,
        copySinkTypes: [],
        copySourceTypes: [adfModelTypeMap.fileSystem.copySourceType],
        supportDatasetV2: true,
        supportGetMetadataActivity: true,
        supportOPDG: GlobalVariables.enableOPDG,
        locationPattern: {
            folderPath: '([^]*)/[^/]*',
            fileName: '(?:[^]*/)?([^/]*)'
        },
        mergeReadSettingsInTrident: true,
        locationPropertiesRequireParameters: ['folderPath', 'fileName'],
        pathRegex: {
            pattern: Constants.fileSharePathValidation,
            message: stringResources.CopyWizard_Dataset_File_Sink_FileSharePathValidationError
        },
        getMetadataActivityReadProperties: ['useBinaryTransfer'],
        deleteActivityReadProperties: commonFileTypeStoreDeleteActivityReadSettings,
        storeReadSettingControls: insertUseBinaryTransfer(getCommonFileTypeStoreReadSettingControls(false, false, true, false, false, { infoLink: ftpDisableChunkingInfoLink })),
        datasetTypeToStoreReadSettingControls: {
            [binaryMetadata.type]: insertUseBinaryTransfer(getCommonBinaryStoreReadSettingControls(false, false, true, false, true, { infoLink: ftpDisableChunkingInfoLink }))
        },
        supportFirstClassParameterExperience: true,
        controls: dynamicContentControls
    };
    return metadata;
};
